import React, { useEffect, useImperativeHandle, useMemo, useRef } from "react";
import type { FC, MouseEventHandler, ReactNode, JSX } from "react";

import cn from "classnames";

import ButtonLabel from "./components/Label/ButtonLabel";
import { mapButtonViewToLoaderView } from "./constants/views";
import { useTimeredAnimation } from "./hooks/useTimeredAnimation";
import { getTimeredStyles } from "./utils/getTimeredStyles";
import { boxForwardRef } from "../../../helpers/common/Box/Box";
import { ButtonCore } from "../../../helpers/common/ButtonCore";
import { useMouseDownAnimation } from "../../../hooks/useMouseDownAnimation";
import {
    buttonSizeToAssetSizeDict,
    buttonSizeToIndicatorSizeDict,
} from "../../../models/sizes";
import { getChildrenRecord } from "../../../utils/react";
import { getLoaderProps } from "../../../utils/ui/getLoaderProps";
import { UniBoxAdditional } from "../../UniBox";

import type {
    ButtonProps,
    ButtonSizes,
    ButtonStates,
    ButtonWidth,
} from "./types";
import type { BoxComponentWithDefault } from "../../../helpers/common/Box/Box";
import type { HTMLButtonProps } from "../../../types/common";

import classes from "./Button.styl";

/** Массив стейтов, при котором наша кнопку дизейблится */
const DISABLED_STATE: ButtonStates[] = ["disabled", "loading"];
export const BUTTON_LABEL_ERROR =
    "В Button не был передан ни текст, ни aria-label";
export const BUTTON_DISPLAY_NAME = "Button";
export const BUTTON_DEFAULT_SIZE = "56";
export const BUTTON_TIMERED_DURATION = 6000;

export interface IButton extends HTMLButtonProps {
    children: ReactNode;
    /** Отвечает за состояние компонента, основанное на комбинации включенных/выключенных параметров */
    state?: ButtonStates;
    /** Отвечает за исходный размер кнопки и значения, которые идут в комбинации с этим размером. Набор значений действителен для размера шрифта/иконок в состоянии Default */
    size?: ButtonSizes;
    /** Длительность анимации в ms в состоянии timered */
    timeredDuration?: number;
    /** Текст для Label в состоянии timered */
    timeredLabelText?: string;
    /** Отвечает за ширину кнопки. Принимает значения в процентах и пикселях, а также строку "auto" */
    width?: ButtonWidth;
    /** Область, реагирующая на взаимодействие с кнопкой, находится по центру компонента и растягивается по ширине и высоте, но не может быть менее 44x44px */
    interactArea?: boolean;
}

/**
 * Базовый компонент, который сообщает пользователю о действии, которое он может совершить. Компонент умеет принимать в себя контент и передавать ему свойства Button.
 *
 * В качестве основного контента передаётся Label, Icon, IndicatorCounter. Они могут отображаться, а могут быть скрыты. В минимальной комплектации Button содержит или Label, или Icon.
 *
 * В качестве `as` ожидаем, что будет передан 'button', 'a' или компонент, реализующий функцию навигации (например, `RoutingLink` на большом вебе).
 * Более подробную информацию можно найти [тут](/docs/документация-разработчикам-дс-инструкция-по-разработке-полиморфных-компонентов--docs).
 *
 * <a href="https://www.figma.com/design/LYZAXE33pUg2cSL5mmUHYq/%F0%9F%8D%91-Buttons-%26-Tabs-%26-Chips?m=auto&node-id=2-284104&t=3ryRFv5yEABAv4kK-1" target="_blank">Спека</a>
 * <br><br>
 * ### Дочерние компоненты
 *
 * | Компонент | Использование |
 * |:-|:-|
 * | ```<Button.Icon>``` | Отображает иконку внутри кнопки, используется в Button и/или в Button.RightAddon. |
 * | ```<Button.IndicatorCounter>``` | Индикатор со счётчиком, используется в Button.RightAddon. По умолчанию view="custom", view изменить нельзя, для кастомизации следует прокинуть customColorsStyles с CSS-переменными для Button.IndicatorCounter.|
 * | ```<Button.Label>``` | Отображает текст внутри кнопки. |
 * | ```<Button.Loader>``` | Обязательный компонент при state="loading", в него передается label для загрузки. |
 * | ```<Button.TopRightIndicator>``` | Индикатор, отображается в правом верхнем углу кнопки. По умолчанию view="primary". |
 * | ```<Button.TopRightIndicatorCounter>``` | Индикатор со счётчиком, отображается в правом верхнем углу кнопки. По умолчанию view="primary". |
 * | ```<Button.RightAddon>``` | Используется для добавления дополнительных элементов справа от текста, таких как Button.Icon, Button.IndicatorCounter и любых других. Компоненты отображаются в том же порядке, в котором они были переданы. |
 */
export const Button = boxForwardRef<
    BoxComponentWithDefault<"button", ButtonProps>
>(
    (
        {
            as = "button",
            type = "button",
            view = "primary",
            size = BUTTON_DEFAULT_SIZE,
            state = "enabled",
            timeredDuration = BUTTON_TIMERED_DURATION,
            width = "auto",
            interactArea = false,
            timeredLabelText,
            children,
            customColorsStyles,
            onMouseDown,
            onMouseUp,
            "aria-label": ariaLabel,
            "data-tsid": tsid,
            ...props
        },
        ref,
    ): JSX.Element => {
        const buttonRef = useRef<HTMLButtonElement>(null);
        useImperativeHandle(ref, () => buttonRef.current);
        const { clicked, mousedownHandler } = useMouseDownAnimation();

        const { isTimeredActive, startAnimation, stopAnimation } =
            useTimeredAnimation(buttonRef, timeredDuration);

        useEffect(() => {
            if (state === "timered") {
                startAnimation();
                return;
            }
            stopAnimation();
        }, [state, startAnimation, stopAnimation]);

        const { TopRightIndicator, TopRightIndicatorCounter, ...restChildren } =
            useMemo(
                () =>
                    getChildrenRecord(children, "Button", {
                        TopRightIndicator: {
                            props: {
                                size: buttonSizeToIndicatorSizeDict[size],
                            },
                        },
                        TopRightIndicatorCounter: {},
                        Unknown: {
                            skipElement: false,
                        },
                    }),
                [size, children],
            );

        const notEnabledStateClasses =
            state !== "enabled" ? { [classes[`__state-${state}`]]: true } : {};

        const buttonCoreWrapperClasses = cn(
            classes["button-container"],
            {
                [classes[`__style-${view}`]]: view,
                [classes["__clicked"]]: clicked,
                ...notEnabledStateClasses,
            },
            customColorsStyles,
        );

        const isFullWidth = width === "100%";

        const wrapperClasses = cn(classes["button-wrapper"], {
            [classes[`__size-${size}`]]: size,
            [classes["__clicked"]]: clicked,
            [classes["__full-width"]]: isFullWidth,
            ...notEnabledStateClasses,
        });

        const timeredStyles = getTimeredStyles(timeredDuration);

        const mouseDownHandler: MouseEventHandler<HTMLButtonElement> = (e) => {
            onMouseDown?.(e);
            mousedownHandler();
        };

        const notEnabledStateWithInteractAreaClasses =
            interactArea && state !== "enabled"
                ? { [classes[`__state-${state}`]]: true }
                : {};

        const buttonCoreContainerClasses = cn(classes["button-element"], {
            [classes["button-interact-area"]]: interactArea,
            ...notEnabledStateWithInteractAreaClasses,
        });

        const isDisabled = DISABLED_STATE.includes(state);
        const isAddons: boolean =
            !!TopRightIndicator || !!TopRightIndicatorCounter;

        return (
            <div className={wrapperClasses} style={{ width }}>
                <ButtonCore
                    as={as}
                    {...props}
                    wrapperClassName={buttonCoreWrapperClasses}
                    containerClassName={buttonCoreContainerClasses}
                    type={type}
                    disabled={isDisabled}
                    onMouseDown={mouseDownHandler}
                    onMouseUp={onMouseUp}
                    data-tsid={tsid}
                    data-uikit={BUTTON_DISPLAY_NAME}
                    aria-label={ariaLabel}
                    ref={buttonRef}
                    style={timeredStyles}
                >
                    <ButtonContent
                        state={state}
                        size={size}
                        aria-label={ariaLabel}
                        isTimeredActive={isTimeredActive}
                        timeredLabelText={timeredLabelText}
                        view={view}
                    >
                        {Object.values(restChildren)}
                    </ButtonContent>
                </ButtonCore>
                <div className={classes["button-outline"]} />
                {isAddons && (
                    <UniBoxAdditional>
                        {TopRightIndicator && (
                            <UniBoxAdditional.SlotTopRight
                                zIndex={1}
                                customVarsStyles={
                                    classes[
                                        "button-uni-box-indicator-top-right"
                                    ]
                                }
                            >
                                {TopRightIndicator}
                            </UniBoxAdditional.SlotTopRight>
                        )}

                        {TopRightIndicatorCounter && (
                            <UniBoxAdditional.SlotTopRight
                                zIndex={1}
                                customVarsStyles={
                                    classes[
                                        "button-uni-box-indicator-counter-top-right"
                                    ]
                                }
                            >
                                {TopRightIndicatorCounter}
                            </UniBoxAdditional.SlotTopRight>
                        )}
                    </UniBoxAdditional>
                )}
            </div>
        );
    },
);

Button.displayName = BUTTON_DISPLAY_NAME;

type IButtonContent = Pick<
    IButton,
    "state" | "size" | "children" | "aria-label" | "timeredLabelText"
> & {
    isTimeredActive: boolean;
} & Pick<ButtonProps, "view">;

const ButtonContent: FC<IButtonContent> = (props): JSX.Element => {
    const {
        size,
        state,
        "aria-label": ariaLabel,
        children,
        isTimeredActive,
        timeredLabelText,
        view,
    } = props;

    const assetSize = buttonSizeToAssetSizeDict[size!];
    const assetView = mapButtonViewToLoaderView[view!];

    const isLoading = state === "loading";

    const { Icon, RightAddon, Label, Loader } = useMemo(
        () =>
            getChildrenRecord(children, BUTTON_DISPLAY_NAME, {
                Label: {
                    optionalRequired: !children,
                    props: {
                        buttonSize: size,
                    },
                },
                Icon: {
                    props: {
                        buttonSize: size,
                        size: assetSize,
                        isRight: false,
                    },
                    optionalRequired: !children,
                },
                RightAddon: {
                    props: {
                        size: assetSize,
                    },
                },
                Loader: {
                    props: getLoaderProps({
                        size: assetSize,
                        view: assetView,
                        customColorsStyles:
                            classes["loader-custom-color-styles"],
                    }),
                    required: isLoading,
                },
                Unknown: {
                    skipElement: false,
                },
            }),
        [assetSize, children, isLoading, size, assetView],
    );

    if (!Label && !ariaLabel) {
        throw new Error(BUTTON_LABEL_ERROR);
    }
    /* eslint-disable ok-custom-rules/check-attribute */
    return (
        <>
            <span
                className={cn(classes["content"], {
                    [classes["__loading"]]: isLoading,
                })}
                data-tsid="button-content-test-id"
            >
                {Icon}
                {isTimeredActive && timeredLabelText ? (
                    <ButtonLabel data-tsid="button-label-timered-test-id">
                        {timeredLabelText}
                    </ButtonLabel>
                ) : (
                    Label
                )}
                {RightAddon}
            </span>
            {isLoading && (
                <span
                    className={classes["loader-container"]}
                    data-tsid="button-loader-container-test-id"
                >
                    {Loader}
                </span>
            )}
        </>
    );
};

export default Button;
